import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  User, Palette, Globe, Download, LogOut, Ruler, Target, Moon, Sun, ShieldAlert,
  Bell, Droplet, CalendarCheck, Scale, Loader2, Utensils, Camera, Trash2, Watch, Smartphone, HeartPulse,
  Users, Heart, Copy,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { SectionHeader } from "@/components/Shared";
import { DeleteAccountCard } from "@/components/DeleteAccountCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function Profile() {
  const { t } = useI18n();
  const { user, refresh, logout } = useAuth();
  const { lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [p, setP] = useState(user?.profile || {});
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  const upd = (k, v) => setP({ ...p, [k]: v });

  const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  const resizeToSquare = (file, size = 512) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const side = Math.min(img.width, img.height);
          const sx = (img.width - side) / 2;
          const sy = (img.height - side) / 2;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(t("photoInvalidType"));
      return;
    }
    if (file.size > 6_000_000) {
      toast.error(t("photoTooLarge"));
      return;
    }
    setUploadingPhoto(true);
    try {
      const image = await resizeToSquare(file);
      await api.post("/user/photo", { image });
      await refresh();
      toast.success(t("photoUpdated"));
    } catch {
      toast.error("Error");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await api.delete("/user/photo");
      await refresh();
      toast.success(t("photoRemoved"));
    } catch {
      toast.error("Error");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { name, username, profile: { ...p, language: lang, theme } });
      await refresh();
      toast.success(t("save"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const exportData = async () => {
    const { data } = await api.get("/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fitpulse-export-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success(t("exportData"));
  };

  const downloadCsv = (rows, filename) => {
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportWorkoutsCsv = async () => {
    const { data } = await api.get("/export");
    const rows = [["date", "workout", "exercise", "set", "weight_kg", "reps", "warmup", "notes"]];
    (data.sessions || []).filter((s) => s.status === "completed").forEach((s) => {
      (s.logs || []).forEach((lg) => {
        (lg.sets || []).forEach((st, i) => {
          rows.push([s.date, s.name, lg.exercise_name, i + 1, st.weight ?? "", st.reps ?? "", st.warmup ? "yes" : "", s.notes || ""]);
        });
      });
    });
    downloadCsv(rows, `fitpulse-latihan-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(t("exportCsvWorkouts"));
  };

  const exportNutritionCsv = async () => {
    const { data } = await api.get("/export");
    const rows = [["date", "meal", "food", "calories", "protein_g", "carbs_g", "fat_g"]];
    (data.nutrition || []).forEach((n) => {
      rows.push([n.date, n.meal || "", n.name || "", n.calories ?? "", n.protein ?? "", n.carbs ?? "", n.fat ?? ""]);
    });
    downloadCsv(rows, `fitpulse-nutrisi-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(t("exportCsvNutrition"));
  };

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };
  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const SelectRow = ({ label, k, options }) => (
    <div className="flex items-center justify-between gap-4 py-1">
      <Label>{label}</Label>
      <Select value={String(p[k] ?? "")} onValueChange={(v) => upd(k, v)}>
        <SelectTrigger className="w-44" data-testid={`profile-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SectionHeader title={t("profile")} subtitle={t("accountSettings")} />

      {/* Identity */}
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.picture} className="object-cover" />
              <AvatarFallback className="bg-primary/15 text-lg font-semibold text-primary">{initials}</AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={() => !uploadingPhoto && fileRef.current?.click()}
              className="absolute -bottom-1.5 -right-1.5 grid h-9 w-9 place-items-center rounded-full border-2 border-card bg-primary text-primary-foreground shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
              disabled={uploadingPhoto}
              aria-label={t("uploadPhoto")}
              data-testid="profile-photo-upload-btn"
            >
              {uploadingPhoto ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="hidden"
              onChange={onPickPhoto}
              data-testid="profile-photo-input"
            />
          </div>
          <div className="flex-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="font-display text-lg font-semibold" data-testid="profile-name" />
            <div className="relative mt-2">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">@</span>
              <Input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20))}
                placeholder={lang === "id" ? "username" : "username"} className="pl-7 text-sm" data-testid="profile-username" />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{lang === "id" ? "Teman bisa menemukanmu lewat username ini." : "Friends can find you by this username."}</p>
            <div className="mt-1 text-sm text-muted-foreground">{user?.email}</div>
            <div className="mt-1.5 flex items-center gap-3">
              {user?.role === "admin" && <span className="inline-block rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">Admin</span>}
              {user?.role === "admin" && <Link to="/store-kit" className="text-xs font-medium text-primary hover:underline" data-testid="profile-store-kit-link">Kit Publish Aplikasi →</Link>}
              {user?.picture && (
                <button
                  type="button"
                  onClick={removePhoto}
                  disabled={uploadingPhoto}
                  className="inline-flex items-center gap-1 text-xs font-medium text-destructive hover:underline disabled:opacity-60"
                  data-testid="profile-photo-remove-btn"
                >
                  <Trash2 className="h-3 w-3" /> {t("removePhoto")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body profile */}
      <Card icon={Target} title={t("setupProfile")}>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>{t("height")} (cm)</Label><Input type="number" value={p.height_cm || ""} onChange={(e) => upd("height_cm", parseFloat(e.target.value) || null)} className="mt-1.5 tabular-nums" data-testid="profile-height" /></div>
          <div><Label>{t("startingWeight")} (kg)</Label><Input type="number" step="0.1" value={p.starting_weight || ""} onChange={(e) => upd("starting_weight", parseFloat(e.target.value) || null)} className="mt-1.5 tabular-nums" data-testid="profile-weight" /></div>
        </div>
        <SelectRow label={t("sex")} k="sex" options={["male", "female"]} />
        <SelectRow label={t("fitnessGoal")} k="goal" options={["lose_fat", "build_muscle", "recomp", "endurance", "general_health"]} />
        <SelectRow label={t("activityLevel")} k="activity_level" options={["sedentary", "light", "moderate", "active", "very_active"]} />
        <SelectRow label={t("experience")} k="experience" options={["beginner", "intermediate", "advanced"]} />
      </Card>

      {/* Preferences */}
      <Card icon={Palette} title={t("settings")}>
        <div className="flex items-center justify-between py-1">
          <Label>{t("theme")}</Label>
          <div className="flex gap-1">
            <button onClick={() => setTheme("light")} className={`grid h-9 w-9 place-items-center rounded-lg border ${theme === "light" ? "border-primary bg-primary/10 text-primary" : ""}`} data-testid="theme-light"><Sun className="h-4 w-4" /></button>
            <button onClick={() => setTheme("dark")} className={`grid h-9 w-9 place-items-center rounded-lg border ${theme === "dark" ? "border-primary bg-primary/10 text-primary" : ""}`} data-testid="theme-dark"><Moon className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="flex items-center justify-between py-1">
          <Label>{t("language")}</Label>
          <div className="flex gap-1">
            {["id", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold uppercase ${lang === l ? "border-primary bg-primary/10 text-primary" : ""}`} data-testid={`lang-${l}`}>{l}</button>
            ))}
          </div>
        </div>
        <SelectRow label={t("units")} k="units" options={["metric", "imperial"]} />
      </Card>

      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-profile">{t("save")}</Button>

      <RemindersCard reminders={p.reminders} onChange={(r) => upd("reminders", r)} />

      <WearablesCard />

      <TrainerModeCard />

      <DonateCard />

      {/* Data & account */}
      <Card icon={Download} title={t("exportData")}>
        <div className="grid gap-3 sm:grid-cols-3">
          <Button variant="outline" onClick={exportData} className="gap-2" data-testid="export-btn"><Download className="h-4 w-4" /> JSON</Button>
          <Button variant="outline" onClick={exportWorkoutsCsv} className="gap-2" data-testid="export-workouts-csv-btn"><Download className="h-4 w-4" /> {t("exportCsvWorkouts")}</Button>
          <Button variant="outline" onClick={exportNutritionCsv} className="gap-2" data-testid="export-nutrition-csv-btn"><Download className="h-4 w-4" /> {t("exportCsvNutrition")}</Button>
        </div>
        <Button variant="outline" onClick={doLogout} className="mt-3 w-full gap-2 text-destructive" data-testid="profile-logout"><LogOut className="h-4 w-4" /> {t("logout")}</Button>
      </Card>

      <DeleteAccountCard />

      <div className="flex items-start gap-3 rounded-2xl border border-dashed p-4 text-xs text-muted-foreground">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {t("disclaimer")}
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="mb-4 flex items-center gap-2 font-display font-semibold"><Icon className="h-4 w-4 text-primary" /> {title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function WearablesCard() {
  const { t } = useI18n();
  const devices = [
    { key: "apple-health", name: "Apple Health", icon: HeartPulse, color: "bg-rose-500/15 text-rose-500" },
    { key: "google-fit", name: "Google Fit", icon: Smartphone, color: "bg-sky-500/15 text-sky-500" },
    { key: "garmin", name: "Garmin", icon: Watch, color: "bg-indigo-500/15 text-indigo-500" },
  ];
  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="wearables-card">
      <h3 className="mb-4 flex items-center gap-2 font-display font-semibold"><Watch className="h-4 w-4 text-primary" /> {t("connectDevices")}</h3>
      <div className="space-y-2.5">
        {devices.map((d) => (
          <div key={d.key} className="flex items-center justify-between rounded-xl border bg-background p-3" data-testid={`device-${d.key}`}>
            <div className="flex items-center gap-3">
              <span className={`grid h-9 w-9 place-items-center rounded-lg ${d.color}`}><d.icon className="h-5 w-5" /></span>
              <span className="text-sm font-medium">{d.name}</span>
            </div>
            <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">{t("comingSoon")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrainerModeCard() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.profile?.trainer_mode);
  const [code, setCode] = useState(null);
  const [loadingCode, setLoadingCode] = useState(false);

  const toggle = async (v) => {
    setEnabled(v);
    try {
      await api.put("/profile", { profile: { trainer_mode: v } });
      await refresh();
      toast.success(t("save"));
    } catch { toast.error("Error"); setEnabled(!v); }
  };

  const showCode = async () => {
    setLoadingCode(true);
    try {
      const { data } = await api.get("/me/client-code");
      setCode(data.code);
    } catch { toast.error("Error"); } finally { setLoadingCode(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); toast.success(t("linkCopied")); } catch { /* noop */ }
  };

  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="trainer-mode-card">
      <h3 className="mb-1 flex items-center gap-2 font-display font-semibold"><Users className="h-4 w-4 text-primary" /> {t("trainerMode")}</h3>
      <p className="mb-4 text-xs text-muted-foreground">{t("trainerModeHint")}</p>
      <div className="flex items-center justify-between py-1">
        <Label>{t("enableTrainerMode")}</Label>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="trainer-mode-toggle" />
      </div>
      <div className="mt-4 rounded-xl border border-dashed p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("yourClientCode")}</div>
        <p className="mt-1 text-xs text-muted-foreground">{t("yourClientCodeHint")}</p>
        {code ? (
          <div className="mt-3 flex items-center gap-2">
            <span className="rounded-lg bg-primary/10 px-4 py-2 font-display text-xl font-bold tracking-widest text-primary" data-testid="client-code-value">{code}</span>
            <Button size="sm" variant="outline" onClick={copyCode} className="gap-1" data-testid="copy-client-code"><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={showCode} disabled={loadingCode} className="mt-3 gap-1.5" data-testid="show-client-code">
            {loadingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t("showClientCode")}
          </Button>
        )}
      </div>
    </div>
  );
}

function DonateCard() {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6" data-testid="donate-card">
      <h3 className="mb-1 flex items-center gap-2 font-display font-semibold"><Heart className="h-4 w-4 text-rose-500" /> {t("supportServer")}</h3>
      <p className="mb-4 text-sm text-muted-foreground">{t("supportServerHint")}</p>
      <a href="https://saweria.co/fitbersama" target="_blank" rel="noopener noreferrer" data-testid="donate-link">
        <Button className="w-full gap-2 bg-rose-500 text-white hover:bg-rose-600"><Heart className="h-4 w-4" /> {t("donateNow")}</Button>
      </a>
    </div>
  );
}

const DEFAULT_REMINDERS = {
  checkin: { enabled: true, hour: 18 },
  weighin: { enabled: true, weekday: 0, hour: 7 },
  water: { enabled: true, start_hour: 9, end_hour: 21 },
  nutrition: { enabled: true, hour: 12 },
};

function HourSelect({ value, onChange, testid }) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(parseInt(v))}>
      <SelectTrigger className="w-24" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-60">
        {[...Array(24)].map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function RemindersCard({ reminders, onChange }) {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [r, setR] = useState({ ...DEFAULT_REMINDERS, ...(reminders || {}) });
  const [saving, setSaving] = useState(false);

  const setPart = (key, patch) => {
    const next = { ...r, [key]: { ...r[key], ...patch } };
    setR(next); onChange(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { profile: { reminders: r } });
      await refresh();
      toast.success(t("save"));
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="mb-4 flex items-center gap-2 font-display font-semibold"><Bell className="h-4 w-4 text-primary" /> {t("reminders")}</h3>
      <div className="space-y-4">
        {/* Check-in */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm font-medium"><CalendarCheck className="h-4 w-4 text-muted-foreground" /> {t("checkinReminder")}</label>
          <div className="flex items-center gap-3">
            {r.checkin.enabled && <><span className="text-xs text-muted-foreground">{t("reminderTime")}</span><HourSelect value={r.checkin.hour} onChange={(h) => setPart("checkin", { hour: h })} testid="rem-checkin-hour" /></>}
            <Switch checked={r.checkin.enabled} onCheckedChange={(v) => setPart("checkin", { enabled: v })} data-testid="rem-checkin-toggle" />
          </div>
        </div>
        {/* Weigh-in */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm font-medium"><Scale className="h-4 w-4 text-muted-foreground" /> {t("weighinReminder")}</label>
          <div className="flex items-center gap-2">
            {r.weighin.enabled && (
              <>
                <Select value={String(r.weighin.weekday)} onValueChange={(v) => setPart("weighin", { weekday: parseInt(v) })}>
                  <SelectTrigger className="w-28" data-testid="rem-weighin-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{days.map((d, i) => <SelectItem key={i} value={String(i)}>{t(d)}</SelectItem>)}</SelectContent>
                </Select>
                <HourSelect value={r.weighin.hour} onChange={(h) => setPart("weighin", { hour: h })} testid="rem-weighin-hour" />
              </>
            )}
            <Switch checked={r.weighin.enabled} onCheckedChange={(v) => setPart("weighin", { enabled: v })} data-testid="rem-weighin-toggle" />
          </div>
        </div>
        {/* Water */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm font-medium"><Droplet className="h-4 w-4 text-muted-foreground" /> {t("waterReminder")}</label>
          <div className="flex items-center gap-2">
            {r.water.enabled && (
              <>
                <span className="text-xs text-muted-foreground">{t("activeHours")}</span>
                <HourSelect value={r.water.start_hour} onChange={(h) => setPart("water", { start_hour: h })} testid="rem-water-start" />
                <span className="text-muted-foreground">–</span>
                <HourSelect value={r.water.end_hour} onChange={(h) => setPart("water", { end_hour: h })} testid="rem-water-end" />
              </>
            )}
            <Switch checked={r.water.enabled} onCheckedChange={(v) => setPart("water", { enabled: v })} data-testid="rem-water-toggle" />
          </div>
        </div>
        {/* Nutrition */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm font-medium"><Utensils className="h-4 w-4 text-muted-foreground" /> {t("nutritionReminder")}</label>
          <div className="flex items-center gap-3">
            {r.nutrition.enabled && <><span className="text-xs text-muted-foreground">{t("reminderTime")}</span><HourSelect value={r.nutrition.hour} onChange={(h) => setPart("nutrition", { hour: h })} testid="rem-nutrition-hour" /></>}
            <Switch checked={r.nutrition.enabled} onCheckedChange={(v) => setPart("nutrition", { enabled: v })} data-testid="rem-nutrition-toggle" />
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 w-full" variant="outline" data-testid="save-reminders">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("save")}
      </Button>
    </div>
  );
}

